import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';

export enum TicketType {
  QUESTION = 'question',
  FEATURE_REQUEST = 'feature_request',
  BUG_REPORT = 'bug_report',
  FEEDBACK = 'feedback',
}

export enum TicketStatus {
  BACKLOG = 'backlog',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface Comment {
  id: string;
  nodeId: string;
  userId: string;
  userName?: string;
  text: string;
  attachments?: any[];
  createdAt: number;
  updatedAt?: number;
  isEdited: boolean;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;
  organizationId: string;
  creatorId: string;
  creator?: {
    id: string;
    name?: string;
    email: string;
  };
  assigneeId?: string;
  assignee?: {
    id: string;
    name?: string;
    email: string;
  };
  tags?: string[];
  resolvedAt?: string;
  resolvedById?: string;
  resolvedBy?: {
    id: string;
    name?: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TicketStatistics {
  total: number;
  byStatus: {
    backlog: number;
    inProgress: number;
    resolved: number;
  };
  byType: {
    question: number;
    featureRequest: number;
    bugReport: number;
    feedback: number;
  };
  byPriority: {
    urgent: number;
    high: number;
    medium: number;
    low: number;
  };
}

/**
 * Store for managing support tickets
 */
class TicketStore {
  // State
  tickets: Ticket[] = [];
  currentTicket: Ticket | null = null;
  ticketComments: Map<string, Comment[]> = new Map();
  statistics: TicketStatistics | null = null;
  loading: boolean = false;
  error: string | null = null;
  
  // UI State
  isCreateDialogOpen: boolean = false;
  isEditingTicket: string | null = null;
  selectedTicketId: string | null = null;
  filterStatus: TicketStatus | null = null;
  filterType: TicketType | null = null;
  filterPriority: TicketPriority | null = null;
  searchQuery: string = '';

  constructor(private rootStore: any) {
    makeAutoObservable(this);
  }

  // Computed values
  get filteredTickets(): Ticket[] {
    let filtered = this.tickets;
    
    if (this.filterStatus) {
      filtered = filtered.filter(t => t.status === this.filterStatus);
    }
    if (this.filterType) {
      filtered = filtered.filter(t => t.type === this.filterType);
    }
    if (this.filterPriority) {
      filtered = filtered.filter(t => t.priority === this.filterPriority);
    }
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }

  get backlogTickets(): Ticket[] {
    return this.tickets.filter(t => t.status === TicketStatus.BACKLOG);
  }

  get inProgressTickets(): Ticket[] {
    return this.tickets.filter(t => t.status === TicketStatus.IN_PROGRESS);
  }

  get resolvedTickets(): Ticket[] {
    return this.tickets.filter(t => t.status === TicketStatus.RESOLVED);
  }

  get isAdmin(): boolean {
    const user = this.rootStore.authStore?.user;
    return user?.role === 'admin' || user?.role === 'owner';
  }

  // API methods
  async fetchTickets(): Promise<void> {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await axios.get('http://localhost:4011/api/tickets', {
        headers: {
          'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
        },
      });
      
      runInAction(() => {
        this.tickets = response.data;
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch tickets';
        this.loading = false;
      });
      console.error('Failed to fetch tickets:', error);
    }
  }

  async fetchStatistics(): Promise<void> {
    try {
      const response = await axios.get('http://localhost:4011/api/tickets/statistics', {
        headers: {
          'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
        },
      });
      
      runInAction(() => {
        this.statistics = response.data;
      });
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }

  async createTicket(data: {
    title: string;
    description: string;
    type: TicketType;
    priority?: TicketPriority;
    tags?: string[];
  }): Promise<Ticket | null> {
    this.loading = true;
    this.error = null;
    
    try {
      const response = await axios.post('http://localhost:4011/api/tickets', data, {
        headers: {
          'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
        },
      });
      
      const newTicket = response.data;
      
      runInAction(() => {
        this.tickets.unshift(newTicket);
        this.loading = false;
        this.isCreateDialogOpen = false;
      });
      
      return newTicket;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to create ticket';
        this.loading = false;
      });
      console.error('Failed to create ticket:', error);
      return null;
    }
  }

  async updateTicket(ticketId: string, updates: Partial<Ticket>): Promise<boolean> {
    try {
      const response = await axios.patch(`http://localhost:4011/api/tickets/${ticketId}`, updates, {
        headers: {
          'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
        },
      });
      
      const updatedTicket = response.data;
      
      runInAction(() => {
        const index = this.tickets.findIndex(t => t.id === ticketId);
        if (index !== -1) {
          this.tickets[index] = updatedTicket;
        }
        if (this.currentTicket?.id === ticketId) {
          this.currentTicket = updatedTicket;
        }
      });
      
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update ticket';
      });
      console.error('Failed to update ticket:', error);
      return false;
    }
  }

  async updateTicketStatus(ticketId: string, status: TicketStatus): Promise<boolean> {
    try {
      const response = await axios.patch(`http://localhost:4011/api/tickets/${ticketId}/status`, 
        { status }, 
        {
          headers: {
            'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
          },
        }
      );
      
      const updatedTicket = response.data;
      
      runInAction(() => {
        const index = this.tickets.findIndex(t => t.id === ticketId);
        if (index !== -1) {
          this.tickets[index] = updatedTicket;
        }
        if (this.currentTicket?.id === ticketId) {
          this.currentTicket = updatedTicket;
        }
      });
      
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update ticket status';
      });
      console.error('Failed to update ticket status:', error);
      return false;
    }
  }

  async deleteTicket(ticketId: string): Promise<boolean> {
    try {
      await axios.delete(`http://localhost:4011/api/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
        },
      });
      
      runInAction(() => {
        this.tickets = this.tickets.filter(t => t.id !== ticketId);
        if (this.currentTicket?.id === ticketId) {
          this.currentTicket = null;
        }
        this.ticketComments.delete(ticketId);
      });
      
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete ticket';
      });
      console.error('Failed to delete ticket:', error);
      return false;
    }
  }

  async fetchTicketComments(ticketId: string): Promise<void> {
    try {
      const response = await axios.get(`http://localhost:4011/api/tickets/${ticketId}/comments`, {
        headers: {
          'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
        },
      });
      
      runInAction(() => {
        this.ticketComments.set(ticketId, response.data);
      });
    } catch (error) {
      console.error('Failed to fetch ticket comments:', error);
    }
  }

  async addComment(ticketId: string, text: string): Promise<boolean> {
    try {
      const response = await axios.post(
        `http://localhost:4011/api/tickets/${ticketId}/comments`,
        { text },
        {
          headers: {
            'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
          },
        }
      );
      
      const newComment = response.data;
      
      runInAction(() => {
        const comments = this.ticketComments.get(ticketId) || [];
        comments.push(newComment);
        this.ticketComments.set(ticketId, comments);
      });
      
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to add comment';
      });
      console.error('Failed to add comment:', error);
      return false;
    }
  }

  async assignTicket(ticketId: string, assigneeId: string): Promise<boolean> {
    try {
      const response = await axios.patch(
        `http://localhost:4011/api/tickets/${ticketId}/assign`,
        { assigneeId },
        {
          headers: {
            'Authorization': `Bearer ${this.rootStore.authStore.accessToken}`,
          },
        }
      );
      
      const updatedTicket = response.data;
      
      runInAction(() => {
        const index = this.tickets.findIndex(t => t.id === ticketId);
        if (index !== -1) {
          this.tickets[index] = updatedTicket;
        }
        if (this.currentTicket?.id === ticketId) {
          this.currentTicket = updatedTicket;
        }
      });
      
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to assign ticket';
      });
      console.error('Failed to assign ticket:', error);
      return false;
    }
  }

  // UI Actions
  setCreateDialogOpen(open: boolean): void {
    this.isCreateDialogOpen = open;
  }

  selectTicket(ticketId: string | null): void {
    this.selectedTicketId = ticketId;
    if (ticketId) {
      this.currentTicket = this.tickets.find(t => t.id === ticketId) || null;
    } else {
      this.currentTicket = null;
    }
  }

  setFilterStatus(status: TicketStatus | null): void {
    this.filterStatus = status;
  }

  setFilterType(type: TicketType | null): void {
    this.filterType = type;
  }

  setFilterPriority(priority: TicketPriority | null): void {
    this.filterPriority = priority;
  }

  setSearchQuery(query: string): void {
    this.searchQuery = query;
  }

  clearFilters(): void {
    this.filterStatus = null;
    this.filterType = null;
    this.filterPriority = null;
    this.searchQuery = '';
  }

  reset(): void {
    this.tickets = [];
    this.currentTicket = null;
    this.ticketComments.clear();
    this.statistics = null;
    this.loading = false;
    this.error = null;
    this.isCreateDialogOpen = false;
    this.isEditingTicket = null;
    this.selectedTicketId = null;
    this.clearFilters();
  }
}

export default TicketStore;
